/**
 * 고객 이용문의 목록 페이지
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import DataTable, { Column } from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import FilterBar, { FilterField } from '../../components/common/FilterBar';
import { inquiryService } from '../../services/inquiryService';
import { Inquiry, InquiryFilter } from '../../types/inquiry';

const InquiriesList: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<InquiryFilter>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['inquiries', filter],
    queryFn: () => inquiryService.getInquiries(filter),
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning">대기중</Badge>;
      case 'in_progress':
        return <Badge bg="info">처리중</Badge>;
      case 'completed':
        return <Badge bg="success">완료</Badge>;
      case 'cancelled':
        return <Badge bg="secondary">취소</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const columns: Column<Inquiry>[] = [
    {
      key: 'id',
      label: 'ID',
      width: '80px',
    },
    {
      key: 'subject',
      label: '제목',
      render: (inquiry) => (
        <span
          style={{ cursor: 'pointer', color: '#00BCD4' }}
          onClick={() => navigate(`/admin/inquiries/${inquiry.id}`)}
        >
          {inquiry.subject}
        </span>
      ),
    },
    {
      key: 'inquiry_type',
      label: '문의 유형',
      render: (inquiry) => (
        <span style={{ color: '#ccc' }}>{inquiry.inquiry_type}</span>
      ),
    },
    {
      key: 'customer',
      label: '고객 정보',
      render: (inquiry) => (
        <div>
          {inquiry.member_name ? (
            <div>
              <span style={{ color: '#ccc' }}>{inquiry.member_name}</span>
              {inquiry.member_email && (
                <div style={{ fontSize: '0.85em', color: '#888' }}>{inquiry.member_email}</div>
              )}
            </div>
          ) : inquiry.guest_name ? (
            <div>
              <span style={{ color: '#ccc' }}>{inquiry.guest_name}</span>
              {inquiry.guest_email && (
                <div style={{ fontSize: '0.85em', color: '#888' }}>{inquiry.guest_email}</div>
              )}
            </div>
          ) : (
            <span style={{ color: '#888' }}>비회원</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      label: '상태',
      render: (inquiry) => getStatusBadge(inquiry.status),
    },
    {
      key: 'created_at',
      label: '등록일',
      render: (inquiry) => (
        <span style={{ color: '#ccc' }}>
          {new Date(inquiry.created_at).toLocaleDateString('ko-KR')}
        </span>
      ),
    },
    {
      key: 'responded_at',
      label: '답변일',
      render: (inquiry) => (
        <span style={{ color: '#ccc' }}>
          {inquiry.responded_at ? new Date(inquiry.responded_at).toLocaleDateString('ko-KR') : '-'}
        </span>
      ),
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'search',
      label: '검색',
      type: 'text',
      placeholder: '제목, 내용으로 검색',
    },
    {
      name: 'inquiry_type',
      label: '문의 유형',
      type: 'text',
      placeholder: '문의 유형으로 검색',
    },
    {
      name: 'status',
      label: '상태',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'pending', label: '대기중' },
        { value: 'in_progress', label: '처리중' },
        { value: 'completed', label: '완료' },
        { value: 'cancelled', label: '취소' },
      ],
    },
  ];

  const handleFilterChange = (values: Record<string, any>) => {
    setFilter((prev) => ({
      ...prev,
      ...values,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleRowClick = (inquiry: Inquiry) => {
    navigate(`/admin/inquiries/${inquiry.id}`);
  };

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>고객 이용문의 관리</h1>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <FilterBar
              fields={filterFields}
              values={filter}
              onChange={handleFilterChange}
              onSearch={() => refetch()}
              onReset={() => setFilter({ page: 1, limit: 20 })}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                총 <strong>{data?.pagination.total_count || 0}</strong>건
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data?.inquiries || []}
              loading={isLoading}
              emptyMessage="문의 내역이 없습니다."
              onRowClick={handleRowClick}
            />

            {data && data.pagination.total_pages > 1 && (
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default InquiriesList;

